import type {
  CreateDocumentMutationResult,
  Document,
  Register,
  RegisterMutation,
  UpdateDocumentMutationResult,
} from 'generated/gql';
import { useUpdateMutation } from 'generated/gql';
import { t } from '@lingui/macro';
import { SidebarFormContainer, SidebarFormContent } from 'components/SidebarForm/SidebarForm';
import { DocumentUploadForm, documentUploadFormSchema } from 'forms/document/DocumentUploadForm';
import { mapDocumentToDocumentInput } from 'graphql/input-mappers/Document';
import { mapMutationToMutationInput } from 'graphql/input-mappers/RegisterMutation';
import { useUpdateDocument } from 'hooks/useUpdateDocument';

type UpdateDocumentResult = NonNullable<UpdateDocumentMutationResult['data']>;
type CreateDocumentResult = NonNullable<CreateDocumentMutationResult['data']>;

export type OnDocumentSaveFnDocument =
  CreateDocumentResult['createDocument'] | UpdateDocumentResult['updateDocument'];
export type OnDocumentSaveFn = (document: OnDocumentSaveFnDocument) => void;

interface DocumentSidebarFormProps {
  register: Pick<Register, 'id' | 'legalEntity'>;
  document?: Document;
  mutation?: RegisterMutation;
  isSidebarShown: boolean;
  onDocumentSave: OnDocumentSaveFn;
  onSidebarClose: () => void;
}
export const DocumentSidebarForm = ({ isSidebarShown, ...props }: DocumentSidebarFormProps) => {
  return (
    <SidebarFormContainer isDrawerShown={isSidebarShown}>
      {isSidebarShown && <DocumentSidebarFormContent {...props} />}
    </SidebarFormContainer>
  );
};

const DocumentSidebarFormContent = ({
  register,
  document,
  mutation,
  onDocumentSave,
  onSidebarClose,
}: Omit<DocumentSidebarFormProps, 'isSidebarShown'>) => {
  const updateDocument = useUpdateDocument(register.id);
  const [updateMutation] = useUpdateMutation();
  const mutationType = document?.mutation?.type;
  const isNew = !document;
  const header = isNew ? t`Document toevoegen` : t`Document bewerken`;

  return (
    <SidebarFormContent
      defaultValues={{ ...document, dossierNumber: mutation?.dossierNumber } as any}
      form={
        <DocumentUploadForm
          canEditFileName
          canChooseType={isNew}
          hasColumns={false}
          isFoundation={register.legalEntity.isFoundation}
          isRequired={!mutationType}
          mutationType={mutationType}
        />
      }
      formSchema={documentUploadFormSchema}
      header={header}
      setDrawerClosed={onSidebarClose}
      onSubmit={async (values) => {
        updateDocument({
          values,
          document,
          onDocumentSave: async (savedDocument) => {
            if (mutation) {
              const mutationInput = {
                ...mapMutationToMutationInput(mutation),
                document: mapDocumentToDocumentInput(savedDocument),
                dossierNumber: values.dossierNumber ?? null,
              };
              await updateMutation({
                variables: { mutationId: mutation.id, mutation: mutationInput },
              });
            }
            onDocumentSave(savedDocument);
            onSidebarClose();
          },
        });
      }}
    />
  );
};
